/**
 * Breadcrumb trail resolver: the shared helpers and the English trails.
 *
 * Client-safe: AutoBreadcrumbs imports this module from the root layout, so
 * nothing here may pull in page datasets or the Hindi name maps
 * (`breadcrumbs-hi.ts`). The full resolver for server and edge callers is
 * `resolveTrail` in `breadcrumbs.ts`.
 */

import { internalHref } from "../../shared/canonical-url";
import { RASHIS } from "./rashi-presentation";

export interface BreadcrumbItem {
  name: string;
  href?: string;
}

export function titleCase(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const RASHI_NAMES: Readonly<Record<string, string>> = Object.fromEntries(
  RASHIS.map((rashi) => [rashi.id, `${titleCase(rashi.id)} (${rashi.name_en})`])
);

export const RASHI_NAMES_HI: Readonly<Record<string, string>> = Object.fromEntries(
  RASHIS.map((rashi) => [rashi.id, rashi.name_hi])
);

/** /calendar/festival/<id> and its Hindi twin; the hub itself keeps its trail. */
const FESTIVAL_DETAIL_PATH = /^(?:\/hi)?\/calendar\/festival\/[^/]+$/;

/** Pages that should not show auto-breadcrumbs */
const NO_BREADCRUMB_SLUGS = new Set([
  "/",
  // The Hindi home is a root, like "/".
  "/hi",
  "/free-kundali",
  "/ai-astrologer",
  "/ai-kundli",
  "/friends",
  "/friends/invite",
]);

/** English single-page names that title-casing the slug gets wrong. */
const EN_PAGE_NAMES: Record<string, string> = {
  "houses-in-astrology": "12 Houses in Astrology",
  "nadi-dosha": "Nadi Dosha",
  // The page's own name, which its BreadcrumbList declares; it renders no trail
  // of its own, so this is its only one (and its ?city= views').
  "shubh-muhurat": "Shubh Muhurat Today",
};

const SMALL_WORDS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "by",
  "for",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
  "vs",
]);

/**
 * "astrologer-for-marriage-problem" -> "Astrologer for Marriage Problem", as the
 * pages' own BreadcrumbList names them. Until 2026-10-11 the callers replaced the
 * hyphens before titleCase split on them, so 43 trails read "Ascendant
 * calculator" over a BreadcrumbList "Ascendant Calculator".
 */
function slugLabel(slug: string): string {
  return slug
    .split("-")
    .map((word, index) =>
      index > 0 && SMALL_WORDS.has(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join(" ");
}

const HOROSCOPE_ROUTES: Record<string, string> = {
  "yearly-rashifal": "Yearly Rashifal",
  "career-horoscope": "Career Horoscope",
  "love-horoscope": "Love Horoscope",
  "marriage-horoscope": "Marriage Horoscope",
  "health-horoscope": "Health Horoscope",
};

/** The pathname without its trailing slash, as the resolvers compare it. */
function trailPath(pathname: string): string {
  return pathname.endsWith("/") && pathname !== "/" ? pathname.slice(0, -1) : pathname;
}

/** Whether `pathname` takes the Hindi resolver (a page under /hi/, not /hi itself). */
export function isHindiTrailPath(pathname: string): boolean {
  return trailPath(pathname).startsWith("/hi/");
}

/**
 * Resolve a breadcrumb trail for a given pathname, with the Hindi resolver
 * passed in. Returns null if the page should not show breadcrumbs. The
 * Hindi resolver lives in `breadcrumbs-hi.ts` so its name maps stay out of
 * the root layout's client graph: AutoBreadcrumbs resolves English paths here
 * and loads the Hindi resolver only on /hi/ pages.
 */
export function resolveTrailWith(
  pathname: string,
  resolveHindiTrail: (path: string) => BreadcrumbItem[] | null
): BreadcrumbItem[] | null {
  // Strip trailing slash from static-export paths
  const path = trailPath(pathname);
  if (NO_BREADCRUMB_SLUGS.has(path)) return null;
  // A festival detail page renders its own breadcrumb (and BreadcrumbList)
  // with the festival's NAME. This resolver only sees the id, and titleCase
  // splits on "-" not "_", so it printed "Lunar_eclipse_sep_2026" above a page
  // about 28 August, and "Tulsi_vivah" on the most-visited festival page.
  if (FESTIVAL_DETAIL_PATH.test(path)) return null;
  const trail = path.startsWith("/hi/") ? resolveHindiTrail(path) : resolveEnglishTrail(path);
  // Every crumb links the canonical slashed URL: a slashless href is a link
  // through a 301 (591 breadcrumb links did this on 2026-09-28).
  return (
    trail?.map((item) => (item.href ? { ...item, href: internalHref(item.href) } : item)) ?? null
  );
}

/**
 * Hindi trails start at the Hindi home, /hi/. Until 2026-09 they started at the
 * English "/" and a "हिंदी" crumb that linked /hi/free-kundali/, because /hi/
 * itself was a redirect.
 */
export const HINDI_HOME_CRUMB: BreadcrumbItem = { name: "होम", href: "/hi/" };

/** Sections whose English trail is Home › Section › Leaf. */
const EN_SIMPLE_SECTIONS: Readonly<Record<string, { name: string; href: string }>> = {
  mahadasha: { name: "Mahadasha", href: "/mahadasha/" },
  dosha: { name: "Doshas", href: "/doshas/" },
  yoga: { name: "Yoga", href: "/yoga/" },
  muhurat: { name: "Shubh Muhurat", href: "/muhurat/" },
};

function resolveEnglishTrail(path: string): BreadcrumbItem[] | null {
  const home: BreadcrumbItem = { name: "Home", href: "/" };
  const segments = path.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const [first, second, third] = segments;
  const directTrail = resolveDirectDynamicTrail(home, first, second, third);
  if (directTrail) return directTrail;

  if (first in HOROSCOPE_ROUTES) return resolveHoroscopeTrail(home, first, second);
  const section = EN_SIMPLE_SECTIONS[first];
  if (section) return resolveSimpleSectionTrail(home, section.name, section.href, second);
  if (first === "compatibility") return resolveCompatibilityTrail(home, second);
  if (first === "calendar") return resolveCalendarTrail(home, second, third);

  // dasha-calculator has its own nested layout — single level
  // All other single-segment paths: Home › Page Title
  return [home, { name: EN_PAGE_NAMES[first] ?? slugLabel(first) }];
}

function resolveDirectDynamicTrail(
  home: BreadcrumbItem,
  first: string,
  second?: string,
  third?: string
) {
  if (first === "rashifal") return resolveRashifalTrail(home, second);
  if (first === "panchang") return resolveCityTrail(home, "Panchang", "/panchang/", second);
  if (first === "choghadiya") return resolveCityTrail(home, "Choghadiya", "/choghadiya/", second);
  if (first === "nakshatra") return resolveNakshatraTrail(home, second, third);
  if (first === "rashi") return resolveRashiTrail(home, second, third);
  if (first === "graha") return resolveGrahaTrail(home, second, third);
  return null;
}

function resolveRashifalTrail(home: BreadcrumbItem, second?: string) {
  if (!second) return [home, { name: "Rashifal" }];
  return [
    home,
    { name: "Rashifal", href: "/rashifal/" },
    { name: RASHI_NAMES[second] || titleCase(second) },
  ];
}

function resolveCityTrail(home: BreadcrumbItem, name: string, href: string, second?: string) {
  if (!second) return [home, { name }];
  return [home, { name, href }, { name: slugLabel(second) }];
}

function resolveNakshatraTrail(home: BreadcrumbItem, second?: string, third?: string) {
  if (!second) return [home, { name: "Nakshatra" }];
  const trail: BreadcrumbItem[] = [home, { name: "Nakshatra", href: "/nakshatra/" }];
  const nakshatraName = slugLabel(second.replace(/-female$|-male$|-compatibility$/, ""));
  trail.push({ name: nakshatraName, href: `/nakshatra/${second}/` });
  if (third) {
    trail.push({ name: third.startsWith("pada-") ? `Pada ${third.slice(5)}` : titleCase(third) });
  }
  return trail;
}

function resolveRashiTrail(home: BreadcrumbItem, second?: string, third?: string) {
  if (!second) return [home, { name: "Rashi" }];
  // /rashi/ is the index of the 12 signs (src/app/rashi/page.tsx).
  const trail: BreadcrumbItem[] = [home, { name: "Rashi", href: "/rashi/" }];
  trail.push({ name: RASHI_NAMES[second] || titleCase(second), href: `/rashi/${second}/` });
  if (third) trail.push({ name: titleCase(third) });
  return trail;
}

function resolveGrahaTrail(home: BreadcrumbItem, second?: string, third?: string) {
  if (!second) return [home, { name: "Graha" }];
  const trail: BreadcrumbItem[] = [home, { name: "Graha", href: "/graha/" }];
  trail.push({ name: titleCase(second), href: `/graha/${second}/` });
  if (third) {
    trail.push({ name: grahaChildName(third) });
  }
  return trail;
}

function grahaChildName(third: string) {
  const houseMatch = third.match(/^in-(\d+)-house$/);
  if (houseMatch) {
    const n = parseInt(houseMatch[1], 10);
    return `${n}${ordinalSuffix(n)} House`;
  }
  if (!third.startsWith("in-")) return titleCase(third);
  const rashiId = third.slice(3);
  return RASHI_NAMES[rashiId] || titleCase(rashiId);
}

function ordinalSuffix(n: number) {
  if (n === 1) return "st";
  if (n === 2) return "nd";
  if (n === 3) return "rd";
  return "th";
}

function resolveHoroscopeTrail(home: BreadcrumbItem, first: string, second?: string) {
  const trail: BreadcrumbItem[] = [
    home,
    { name: "Rashifal", href: "/rashifal/" },
    { name: HOROSCOPE_ROUTES[first], href: `/${first}/` },
  ];
  if (second) {
    trail.push({ name: horoscopeChildName(second) });
  }
  return trail;
}

function horoscopeChildName(second: string) {
  const match = second.match(/^([a-z]+)-(\d{4})$/);
  if (!match) return titleCase(second);
  const rashi = RASHI_NAMES[match[1]] || titleCase(match[1]);
  return `${rashi} ${match[2]}`;
}

function resolveSimpleSectionTrail(
  home: BreadcrumbItem,
  name: string,
  href: string,
  second?: string
) {
  const trail: BreadcrumbItem[] = [home, { name, href }];
  if (second) {
    trail.push({
      name: name === "Mahadasha" ? `${titleCase(second)} Mahadasha` : titleCase(second),
    });
  }
  return trail;
}

function resolveCompatibilityTrail(home: BreadcrumbItem, second?: string) {
  const trail: BreadcrumbItem[] = [home, { name: "Compatibility", href: "/compatibility/" }];
  if (second) trail.push({ name: compatibilityPairName(second, RASHI_NAMES) });
  return trail;
}

export function compatibilityPairName(slug: string, names: Record<string, string>) {
  return slug
    .split("-and-")
    .map((segment) => names[segment] || titleCase(segment))
    .join(" & ");
}

function resolveCalendarTrail(home: BreadcrumbItem, second?: string, third?: string) {
  const trail: BreadcrumbItem[] = [home, { name: "Hindu Calendar", href: "/calendar/" }];
  if (second === "festival") {
    // "Festivals" is the festival directory, not a second link to /calendar/;
    // on the directory itself it is the current page and stays unlinked.
    trail.push(third ? { name: "Festivals", href: "/calendar/festival/" } : { name: "Festivals" });
    if (third) trail.push({ name: titleCase(third) });
  }
  return trail;
}
